import { useEffect } from 'react'
import { useForm, Controller, useFieldArray } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, TextField, MenuItem,
  Alert, CircularProgress, IconButton, Autocomplete, Box, Typography, Chip, Divider,
  Table, TableHead, TableRow, TableCell, TableBody,
} from '@mui/material'
import { Add, DeleteOutlined } from '@mui/icons-material'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  createContract, updateContract, getContract,
  type SupplierContractListItem, type SaveSupplierContractPayload, type SupplierContractStatus,
} from '../../api/contracte'
import { listSuppliers } from '../../api/furnizori'
import { listArticles, listUnits } from '../../api/nomenclatoare'
import { errorMessage } from '../../api/client'

const TYPE_LABEL: Record<string, string> = {
  CadruPret: 'Cadru de preț', AngajamentVolum: 'Angajament de volum', Servicii: 'Servicii', Mentenanta: 'Mentenanță',
}
const STATUS_LABEL: Record<SupplierContractStatus, string> = {
  Draft: 'Ciornă', PendingApproval: 'În aprobare', Activ: 'Activ', Expirat: 'Expirat', Reziliat: 'Reziliat',
}
const STATUS_COLOR: Record<SupplierContractStatus, 'default' | 'success' | 'warning' | 'error'> = {
  Draft: 'default', PendingApproval: 'warning', Activ: 'success', Expirat: 'error', Reziliat: 'error',
}

const lineSchema = z.object({
  articleId: z.string().min(1, 'Selectați un articol'),
  negotiatedPrice: z.string().min(1, 'Câmp obligatoriu'),
  currency: z.string().min(1, 'Câmp obligatoriu'),
  unitOfMeasureId: z.string().min(1, 'Selectați o UM'),
  validFrom: z.string().min(1, 'Câmp obligatoriu'),
  committedQuantity: z.string().optional(),
})
const schema = z.object({
  supplierId: z.string().min(1, 'Selectați un furnizor'),
  type: z.enum(['CadruPret', 'AngajamentVolum', 'Servicii', 'Mentenanta']),
  startDate: z.string().min(1, 'Câmp obligatoriu'),
  endDate: z.string().min(1, 'Câmp obligatoriu'),
  currency: z.string().min(1, 'Câmp obligatoriu'),
  paymentTerms: z.string().optional(),
  deliveryTerms: z.string().optional(),
  notes: z.string().optional(),
  leadTimeDaysGuaranteed: z.string().optional(),
  maintenanceResponseTimeHours: z.string().optional(),
  lines: z.array(lineSchema).min(1, 'Adăugați cel puțin o linie'),
})
type FormValues = z.infer<typeof schema>

const EMPTY: FormValues = {
  supplierId: '', type: 'CadruPret', startDate: new Date().toISOString().slice(0, 10), endDate: '',
  currency: 'RON', paymentTerms: '', deliveryTerms: '', notes: '', leadTimeDaysGuaranteed: '',
  maintenanceResponseTimeHours: '', lines: [],
}

export default function ContractFormDialog({ target, onClose }: { target: SupplierContractListItem | null | 'new'; onClose: () => void }) {
  const open = target !== null
  const editing = target !== null && target !== 'new'
  const qc = useQueryClient()

  const { data: suppliers } = useQuery({ queryKey: ['achizitii', 'furnizori', 'toti'], queryFn: () => listSuppliers(), enabled: open })
  const { data: articles } = useQuery({ queryKey: ['nomenclatoare', 'articole', 'toate'], queryFn: () => listArticles(), enabled: open })
  const { data: units } = useQuery({ queryKey: ['nomenclatoare', 'um'], queryFn: listUnits, enabled: open })
  const { data: detail } = useQuery({
    queryKey: ['achizitii', 'contracte', editing ? (target as SupplierContractListItem).id : null],
    queryFn: () => getContract((target as SupplierContractListItem).id),
    enabled: editing,
  })

  const { control, handleSubmit, reset, formState: { errors } } = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: EMPTY })
  const lines = useFieldArray({ control, name: 'lines' })
  const readOnly = editing && detail != null && detail.status !== 'Draft'

  useEffect(() => {
    if (editing && detail) {
      reset({
        supplierId: detail.supplierId, type: detail.type, startDate: detail.startDate, endDate: detail.endDate,
        currency: detail.currency, paymentTerms: detail.paymentTerms ?? '', deliveryTerms: detail.deliveryTerms ?? '',
        notes: detail.notes ?? '', leadTimeDaysGuaranteed: detail.leadTimeDaysGuaranteed != null ? String(detail.leadTimeDaysGuaranteed) : '',
        maintenanceResponseTimeHours: detail.maintenanceResponseTimeHours != null ? String(detail.maintenanceResponseTimeHours) : '',
        lines: detail.lines.map((l) => ({
          articleId: l.articleId, negotiatedPrice: String(l.negotiatedPrice), currency: l.currency,
          unitOfMeasureId: l.unitOfMeasureId, validFrom: l.validFrom, committedQuantity: l.committedQuantity != null ? String(l.committedQuantity) : '',
        })),
      })
    } else if (target === 'new') {
      reset(EMPTY)
    }
  }, [target, editing, detail, reset])

  const mutation = useMutation({
    mutationFn: async (values: FormValues) => {
      const payload: SaveSupplierContractPayload = {
        supplierId: values.supplierId, type: values.type, startDate: values.startDate, endDate: values.endDate,
        currency: values.currency, paymentTerms: values.paymentTerms || null, deliveryTerms: values.deliveryTerms || null,
        notes: values.notes || null,
        leadTimeDaysGuaranteed: values.leadTimeDaysGuaranteed ? Number(values.leadTimeDaysGuaranteed) : null,
        maintenanceResponseTimeHours: values.maintenanceResponseTimeHours ? Number(values.maintenanceResponseTimeHours) : null,
        lines: values.lines.map((l) => ({
          articleId: l.articleId, negotiatedPrice: Number(l.negotiatedPrice), currency: l.currency,
          unitOfMeasureId: l.unitOfMeasureId, validFrom: l.validFrom,
          committedQuantity: l.committedQuantity ? Number(l.committedQuantity) : null,
        })),
      }
      if (editing) return updateContract((target as SupplierContractListItem).id, payload)
      return createContract(payload)
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['achizitii', 'contracte'] })
      onClose()
    },
  })

  if (editing && !detail) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogContent><Stack sx={{ py: 4, alignItems: 'center' }}><CircularProgress /></Stack></DialogContent>
      </Dialog>
    )
  }

  if (readOnly && detail) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          Contract {detail.number}{' '}
          <Chip size="small" label={STATUS_LABEL[detail.status]} color={STATUS_COLOR[detail.status]} variant="outlined" sx={{ ml: 1 }} />
          {detail.isExpiringSoon && <Chip size="small" label="Expiră curând" color="warning" sx={{ ml: 1 }} />}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <Stack direction="row" spacing={4} sx={{ flexWrap: "wrap" }}>
              <Typography variant="body2"><b>Furnizor:</b> {detail.supplierName}</Typography>
              <Typography variant="body2"><b>Tip:</b> {TYPE_LABEL[detail.type] ?? detail.type}</Typography>
              <Typography variant="body2"><b>Perioadă:</b> {detail.startDate} — {detail.endDate}</Typography>
              <Typography variant="body2"><b>Monedă:</b> {detail.currency}</Typography>
            </Stack>
            <Stack direction="row" spacing={4} sx={{ flexWrap: "wrap" }}>
              <Typography variant="body2"><b>Termeni de plată:</b> {detail.paymentTerms ?? '—'}</Typography>
              <Typography variant="body2"><b>Condiții de livrare:</b> {detail.deliveryTerms ?? '—'}</Typography>
              <Typography variant="body2"><b>Lead time garantat:</b> {detail.leadTimeDaysGuaranteed != null ? `${detail.leadTimeDaysGuaranteed} zile` : '—'}</Typography>
              <Typography variant="body2"><b>Timp răspuns mentenanță:</b> {detail.maintenanceResponseTimeHours != null ? `${detail.maintenanceResponseTimeHours} h` : '—'}</Typography>
            </Stack>
            {detail.notes && <Typography variant="body2"><b>Note:</b> {detail.notes}</Typography>}

            <Divider />

            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Articol</TableCell><TableCell align="right">Preț negociat</TableCell>
                  <TableCell>UM</TableCell><TableCell>Valabil de la</TableCell><TableCell>Valabil până la</TableCell>
                  <TableCell align="right">Cant. angajată</TableCell><TableCell align="right">Consumat</TableCell><TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {detail.lines.map((l) => (
                  <TableRow key={l.id}>
                    <TableCell>{l.articleCode} — {l.articleDescription}</TableCell>
                    <TableCell align="right">{l.negotiatedPrice.toFixed(2).replace('.', ',')} {l.currency}</TableCell>
                    <TableCell>{l.unitOfMeasureCode}</TableCell>
                    <TableCell>{l.validFrom}</TableCell>
                    <TableCell>{l.validTo ?? '—'}</TableCell>
                    <TableCell align="right">{l.committedQuantity ?? '—'}</TableCell>
                    <TableCell align="right">{l.consumedQuantity}</TableCell>
                    <TableCell>{l.isCurrent && <Chip size="small" label="Curent" color="success" variant="outlined" />}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={onClose}>Închide</Button>
        </DialogActions>
      </Dialog>
    )
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <form onSubmit={handleSubmit((v) => mutation.mutate(v))}>
        <DialogTitle>{editing ? 'Editare contract furnizor' : 'Contract furnizor nou'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <Stack direction="row" spacing={2}>
              <Controller name="supplierId" control={control} render={({ field }) => (
                <Autocomplete
                  sx={{ flexGrow: 1 }} options={suppliers?.map((s) => s.id) ?? []}
                  getOptionLabel={(id) => suppliers?.find((s) => s.id === id)?.name ?? id}
                  value={field.value || null} onChange={(_, v) => field.onChange(v ?? '')}
                  renderInput={(params) => <TextField {...params} label="Furnizor" error={!!errors.supplierId} helperText={errors.supplierId?.message} />}
                />
              )} />
              <Controller name="type" control={control} render={({ field }) => (
                <TextField {...field} select label="Tip" sx={{ width: 200 }} error={!!errors.type} helperText={errors.type?.message}>
                  <MenuItem value="CadruPret">Cadru de preț</MenuItem>
                  <MenuItem value="AngajamentVolum">Angajament de volum</MenuItem>
                  <MenuItem value="Servicii">Servicii</MenuItem>
                  <MenuItem value="Mentenanta">Mentenanță</MenuItem>
                </TextField>
              )} />
            </Stack>
            <Stack direction="row" spacing={2}>
              <Controller name="startDate" control={control} render={({ field }) => (
                <TextField {...field} type="date" label="Început" sx={{ width: 170 }} slotProps={{ inputLabel: { shrink: true } }}
                  error={!!errors.startDate} helperText={errors.startDate?.message} />
              )} />
              <Controller name="endDate" control={control} render={({ field }) => (
                <TextField {...field} type="date" label="Sfârșit" sx={{ width: 170 }} slotProps={{ inputLabel: { shrink: true } }}
                  error={!!errors.endDate} helperText={errors.endDate?.message} />
              )} />
              <Controller name="currency" control={control} render={({ field }) => (
                <TextField {...field} label="Monedă" sx={{ width: 100 }} error={!!errors.currency} helperText={errors.currency?.message} />
              )} />
              <Controller name="paymentTerms" control={control} render={({ field }) => (
                <TextField {...field} label="Termeni de plată" sx={{ flexGrow: 1 }} />
              )} />
            </Stack>
            <Stack direction="row" spacing={2}>
              <Controller name="deliveryTerms" control={control} render={({ field }) => (
                <TextField {...field} label="Condiții de livrare (Incoterms)" sx={{ flexGrow: 1 }} />
              )} />
              <Controller name="leadTimeDaysGuaranteed" control={control} render={({ field }) => (
                <TextField {...field} type="number" label="Lead time garantat (zile)" sx={{ width: 200 }} />
              )} />
              <Controller name="maintenanceResponseTimeHours" control={control} render={({ field }) => (
                <TextField {...field} type="number" label="Timp răspuns mentenanță (h)" sx={{ width: 220 }} />
              )} />
            </Stack>
            <Controller name="notes" control={control} render={({ field }) => (
              <TextField {...field} label="Note" fullWidth multiline minRows={2} />
            )} />

            {typeof errors.lines?.message === 'string' && <Alert severity="warning">{errors.lines.message}</Alert>}
            {lines.fields.map((f, i) => (
              <Stack key={f.id} direction="row" spacing={1.5} sx={{ alignItems: 'flex-start' }}>
                <Controller name={`lines.${i}.articleId`} control={control} render={({ field }) => (
                  <Autocomplete
                    sx={{ flexGrow: 1 }} options={articles?.map((a) => a.id) ?? []}
                    getOptionLabel={(id) => { const a = articles?.find((x) => x.id === id); return a ? `${a.code} — ${a.shortDescription}` : id }}
                    value={field.value || null} onChange={(_, v) => field.onChange(v ?? '')}
                    renderInput={(params) => <TextField {...params} label="Articol" error={!!errors.lines?.[i]?.articleId}
                      helperText={errors.lines?.[i]?.articleId?.message} />}
                  />
                )} />
                <Controller name={`lines.${i}.negotiatedPrice`} control={control} render={({ field }) => (
                  <TextField {...field} type="number" label="Preț" sx={{ width: 110 }} error={!!errors.lines?.[i]?.negotiatedPrice}
                    helperText={errors.lines?.[i]?.negotiatedPrice?.message} />
                )} />
                <Controller name={`lines.${i}.unitOfMeasureId`} control={control} render={({ field }) => (
                  <Autocomplete
                    sx={{ width: 110 }} options={units?.map((u) => u.id) ?? []}
                    getOptionLabel={(id) => units?.find((u) => u.id === id)?.code ?? id}
                    value={field.value || null} onChange={(_, v) => field.onChange(v ?? '')}
                    renderInput={(params) => <TextField {...params} label="UM" error={!!errors.lines?.[i]?.unitOfMeasureId}
                      helperText={errors.lines?.[i]?.unitOfMeasureId?.message} />}
                  />
                )} />
                <Controller name={`lines.${i}.validFrom`} control={control} render={({ field }) => (
                  <TextField {...field} type="date" label="Valabil de la" sx={{ width: 160 }} slotProps={{ inputLabel: { shrink: true } }}
                    error={!!errors.lines?.[i]?.validFrom} helperText={errors.lines?.[i]?.validFrom?.message} />
                )} />
                <Controller name={`lines.${i}.committedQuantity`} control={control} render={({ field }) => (
                  <TextField {...field} type="number" label="Cant. angajată (opțional)" sx={{ width: 180 }} />
                )} />
                <IconButton onClick={() => lines.remove(i)} sx={{ mt: 0.5 }}><DeleteOutlined fontSize="small" /></IconButton>
              </Stack>
            ))}
            <Box>
              <Button size="small" startIcon={<Add />}
                onClick={() => lines.append({ articleId: '', negotiatedPrice: '', currency: 'RON', unitOfMeasureId: '', validFrom: new Date().toISOString().slice(0, 10), committedQuantity: '' })}>
                Adaugă linie
              </Button>
            </Box>

            {Object.keys(errors).length > 0 && (
              <Alert severity="warning">Există câmpuri obligatorii necompletate — verificați câmpurile marcate cu roșu mai sus.</Alert>
            )}
            {mutation.isError && <Alert severity="error">{errorMessage(mutation.error)}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={onClose}>Renunță</Button>
          <Button type="submit" variant="contained" disabled={mutation.isPending}
            startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}>
            Salvează
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
